import { useRef, useState } from 'react'
import type { GetStaticProps, GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  createStyles,
  Grow,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core'
import { ArrowDropDownRounded } from '@material-ui/icons'
import Bugsnag from '../../utils/bugsnag'
import { getPlayerLadderInfo } from '../../api/server'
import Ladder from '../../components/Ladder'
import Layout from '../../components/Layout'
import Meta from '../../components/Meta'
import type { Player, PlayerLadder } from '../../types'
import {
  genLadderSlug,
  getLadderDescription,
  getLadderIdFromSlug,
  getLadderImageUrl,
  getLadderUrl,
  toStorageTiers,
} from '../../utils/player'
import { getBlankStorageLadder } from '../../utils/storage'
import { LOCAL_STORAGE_KEY } from '../../utils/reducer'
import Share from '../../components/Share'
import AlertDialog from '../../components/AlertDialog'
import PlayerList from '../../components/PlayerList'
import { formatDate } from '../../utils'

const MAX_SNUBS = 5

const CYO_OPTIONS = [
  { label: 'Create your own ladder' },
  {
    label: 'Clone ladder to edit',
    onClick: (ladder: PlayerLadder) => {
      // Update localStorage with the tiers so that when
      // they redirect, /create will pull from localStorage.
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
          ...getBlankStorageLadder(),
          tiers: toStorageTiers(ladder.tiers),
          from: ladder.id,
        }),
      )
    },
  },
]

const CYOButton = ({ ladder }: { ladder: PlayerLadder }) => {
  const router = useRouter()
  const [menuOpen, setMenuOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [alertDialogMessage, setAlertDialogMessage] = useState('')

  const { label, onClick } = CYO_OPTIONS[selectedIndex]

  return (
    <>
      <ButtonGroup variant="contained" color="primary" ref={anchorRef}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            try {
              onClick?.(ladder)
              router.push('/create')
            } catch (err) {
              Bugsnag.notify(err, (event) => {
                event.addMetadata('state', {
                  ...ladder,
                  tiers: toStorageTiers(ladder.tiers),
                })
              })
              setAlertDialogMessage(
                'An error occurred while trying to clone the ladder. Please try again.',
              )
            }
          }}
        >
          {label}
        </Button>
        <Button
          color="primary"
          size="small"
          aria-controls={menuOpen ? 'create-menu' : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-label="Choose what to do next"
          aria-haspopup="menu"
          onClick={() => setMenuOpen((curOpen) => !curOpen)}
        >
          <ArrowDropDownRounded />
        </Button>
        <Popper
          open={menuOpen}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener
                  onClickAway={(e) => {
                    if (anchorRef.current?.contains(e.target as HTMLElement)) {
                      return
                    }

                    setMenuOpen(false)
                  }}
                >
                  <MenuList id="create-menu">
                    {CYO_OPTIONS.map((option, index) => (
                      <MenuItem
                        key={option.label}
                        selected={index === selectedIndex}
                        onClick={(e) => {
                          setSelectedIndex(index)
                          setMenuOpen(false)
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </ButtonGroup>

      <AlertDialog
        open={!!alertDialogMessage}
        onClose={() => setAlertDialogMessage('')}
      >
        {alertDialogMessage}
      </AlertDialog>
    </>
  )
}

const useStyles = makeStyles(({ spacing, breakpoints }) =>
  createStyles({
    titleBar: {
      display: 'grid',
      gap: spacing(2),
      marginTop: spacing(2),

      [breakpoints.up('sm')]: {
        alignItems: 'center',
        gridTemplateColumns: '1fr auto',
      },
    },
  }),
)

interface Props {
  ladder: PlayerLadder
  snubs: Player[]
}

const LadderPage = ({ ladder, snubs }: Props) => {
  const classes = useStyles()
  const url = getLadderUrl(ladder, true)
  const description = getLadderDescription(ladder)
  const shortDescription = getLadderDescription(ladder, true)
  const imageUrl = getLadderImageUrl(ladder)

  return (
    <Layout maxWidth="lg" showAds>
      <Meta
        title={ladder.title}
        url={url}
        imageUrl={imageUrl}
        description={description}
        shortDescription={shortDescription}
      />

      <Box className={classes.titleBar}>
        <Typography component="h1" variant="h3">
          {ladder.title}
        </Typography>
        <Share
          imageUrl={imageUrl}
          shortSummary={shortDescription}
          summary={description}
          title={ladder.title}
          url={url}
        />
      </Box>

      <Box mt={2}>
        <Ladder tiers={ladder.tiers} />
      </Box>

      {snubs.length > 0 && (
        <Box mt={4} display="flex" flexDirection="column" alignItems="center">
          <Typography component="h2" variant="h4">
            Snubs
          </Typography>

          <PlayerList players={snubs.slice(0, MAX_SNUBS)} />
        </Box>
      )}

      <Box mt={2} textAlign="center">
        <Typography component="p" variant="h5" gutterBottom>
          Disagree?
        </Typography>
        <CYOButton ladder={ladder} />
      </Box>

      <Box mt={2}>
        <Typography variant="body2">
          Published: {formatDate(ladder.modified)}
        </Typography>
      </Box>
    </Layout>
  )
}

export default LadderPage

const ONE_DAY = 60 * 60 * 24

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],

  // do server-side rendering for the first render, cache,
  // and do static-rendering from then on
  fallback: 'blocking',
})

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug
  const ladderId =
    !slug || typeof slug !== 'string' ? undefined : getLadderIdFromSlug(slug)

  const ladderInfo = ladderId ? await getPlayerLadderInfo(ladderId) : undefined

  if (!ladderInfo) {
    return { notFound: true }
  }

  const { ladder, snubs } = ladderInfo
  const realSlug = genLadderSlug(ladder)

  // if the slug wasn't correct (the title was wrong),
  // redirect to the correct slug
  if (slug !== realSlug) {
    return {
      redirect: { destination: getLadderUrl(ladder), permanent: true },
    }
  }

  return {
    props: {
      ladder,
      snubs,
    },

    // cache the ladder page for a day to limit
    // firebase data retrievals
    revalidate: ONE_DAY,
  }
}
